import { z } from 'zod';

export type RequiredField = {
  name: string;
  label: string;
  default?: unknown;
  type?: string;
  min?: unknown;
  max?: unknown;
};

const isFilled = (value: unknown, rf: RequiredField): boolean => {
  let val = value;
  if ((val === null || val === undefined || val === '') && rf.default) {
    val = rf.default;
  }
  if (val === null || val === undefined || val === '') {
    return false;
  }
  if (Array.isArray(val) && !val.every((item) => item !== null && item !== '')) {
    return false;
  }
  return true;
};

const buildFieldValidator = (rf: RequiredField) => {
  let validator: z.ZodType = z.any().refine((value) => isFilled(value, rf), {
    error: `Please fill in the ${rf.label} field before proceeding`,
  });

  if (rf.type === 'number' && typeof rf.min === 'number' && typeof rf.max === 'number') {
    const min = rf.min;
    const max = rf.max;
    validator = validator.refine(
      (value) => {
        if (value === null || value === undefined || value === '') return true;
        const n = Number(value);
        return !isNaN(n) && n >= min && n <= max;
      },
      { error: `${rf.label} must be between ${min} and ${max}` },
    );
  }

  return validator;
};

export const validateRequiredFields = (
  requiredFields: RequiredField[],
  formData: unknown,
  getFormDataItem: (data: unknown, name: string) => unknown,
): { ok: boolean; message?: string } => {
  const shape: Record<string, z.ZodType> = {};
  const flatValues: Record<string, unknown> = {};

  requiredFields.forEach((rf) => {
    flatValues[rf.name] = getFormDataItem(formData, rf.name);
    shape[rf.name] = buildFieldValidator(rf);
  });

  const result = z.object(shape).safeParse(flatValues);
  if (result.success) {
    return { ok: true };
  }
  return { ok: false, message: result.error.issues[0]?.message };
};
